import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideIconComponent } from '../../../shared/ui/lucide-icon/lucide-icon.component';
import { AsideService, ViewTeamMemberAsideData } from '../../../core/services/aside.service';
import { UserService } from '../../../core/services/user.service';
import { AuthService } from '../../../core/services/auth.service';
import { PermissionsService } from '../../../core/services/permissions.service';
import { ModalService } from '../../../core/services/modal.service';
import { ToastService } from '../../../shared/ui/toast/toast.service';
import { HeadingComponent } from '../../../shared/ui/typography/heading/heading.component';
import { TextComponent } from '../../../shared/ui/typography/text/text.component';
import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { BadgeComponent } from '../../../shared/ui/badge/badge.component';
import { EditRolesModalComponent } from '../edit-roles-modal/edit-roles-modal.component';
import { ROLE_LABELS, UserRole } from '../../../core/models/user.model';
import { ColorVariant } from '../../../core/enums/types';
import { FormatPhonePipe } from '../../../shared/pipes/format-phone.pipe';

@Component({
  selector: 'app-team-aside',
  standalone: true,
  imports: [CommonModule, 
    LucideIconComponent,
    HeadingComponent,
    TextComponent,
    ButtonComponent,
    BadgeComponent,
    FormatPhonePipe],
  template: `
    @if (member(); as user) {
    <div class="flex flex-col gap-4 p-4 h-full overflow-y-auto">
      <!-- Header: Avatar + Name -->
      <div class="flex items-center gap-4 pb-4 border-b border-border/30">
        <div class="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xl shrink-0">
          {{ (user.fullName || '?')[0] | uppercase }}
        </div>
        <div class="min-w-0">
          <app-heading level="h3">{{ user.fullName || 'Unnamed' }}</app-heading>
          @if (user.id === currentUserId()) {
          <app-text size="xs" color="muted">(You)</app-text>
          }
        </div>
      </div>

      <!-- Contact Info -->
      <div class="space-y-3 bg-surface-high rounded-lg p-3">
        <app-text size="xs" weight="semibold" color="muted" class="uppercase tracking-wider">Contact</app-text>
        <div class="space-y-2">
          @if (user.contact?.email) {
          <div class="flex items-center gap-2.5">
            <lucide-icon name="Mail" class="w-4 h-4 text-muted shrink-0"></lucide-icon>
            <app-text size="sm">{{ user.contact?.email }}</app-text>
          </div>
          }
          @if (user.contact?.phone || user.phone) {
          <div class="flex items-center gap-2.5">
            <lucide-icon name="Phone" class="w-4 h-4 text-muted shrink-0"></lucide-icon>
            <app-text size="sm">{{ (user.contact?.phone || user.phone) | formatPhone }}</app-text>
          </div>
          }
          @if (!user.contact?.email && !user.contact?.phone && !user.phone) {
          <app-text size="sm" color="muted" class="italic">No contact info provided</app-text>
          }
        </div>
      </div>

      <!-- Driver Info -->
      @if (user.cdlInfo?.number) {
      <div class="space-y-3 bg-surface-high rounded-lg p-3">
        <app-text size="xs" weight="semibold" color="muted" class="uppercase tracking-wider">Driver Details</app-text>
        <div class="space-y-2">
          <div class="flex items-center justify-between gap-2.5">
            <app-text size="sm" color="muted">CDL Number</app-text>
            <app-text size="sm" class="font-mono">{{ user.cdlInfo?.number }}</app-text>
          </div>
        </div>
      </div>
      }

      <!-- Roles -->
      @if (canViewRoles()) {
      <div class="space-y-3 bg-surface-high rounded-lg p-3">
        <app-text size="xs" weight="semibold" color="muted" class="uppercase tracking-wider">Roles</app-text>
        <div class="flex flex-wrap gap-1.5">
          @for (role of user.currentCompanyRoles; track role) {
          <app-badge [variant]="getRoleBadgeVariant(role)" [title]="roleLabels[role] || role" size="sm" />
          }
          @empty {
          <app-text size="sm" color="muted" class="italic">No roles assigned</app-text>
          }
        </div>
      </div>
      }

      <!-- Account Details -->
      <div class="space-y-3 bg-surface-high rounded-lg p-3">
        <app-text size="xs" weight="semibold" color="muted" class="uppercase tracking-wider">Account</app-text>
        <div class="space-y-2">
          <div class="flex justify-between">
            <app-text size="sm" color="muted">Status</app-text>
            <app-badge variant="success" title="Active" size="xs" />
          </div>
          @if (user.createdAt) {
          <div class="flex justify-between">
            <app-text size="sm" color="muted">Joined</app-text>
            <app-text size="sm">{{ getCreatedAt(user) | date:'mediumDate' }}</app-text>
          </div>
          }
        </div>
      </div>

      <!-- Actions -->
      @if (canManage() && user.id !== currentUserId()) {
      <div class="flex gap-2 pt-2 mt-auto border-t border-border/30">
        <app-button variant="secondary" size="sm" prefixIcon="Pencil" (click)="editRoles()" class="flex-1">
          Edit Roles
        </app-button>
        <app-button variant="danger-subtle" size="sm" prefixIcon="UserMinus" (click)="removeUser()">
          Remove
        </app-button>
      </div>
      }
    </div>
    } @else {
    <div class="flex flex-col items-center justify-center h-full text-center p-6">
      <lucide-icon name="Users" class="w-12 h-12 text-muted opacity-30 mb-3"></lucide-icon>
      <app-text color="muted" size="sm">Select a team member to view details</app-text>
    </div>
    }
  `,
})
export class TeamAsideComponent {
  private readonly asideService = inject(AsideService);
  private readonly userService = inject(UserService);
  private readonly authService = inject(AuthService);
  private readonly perms = inject(PermissionsService);
  private readonly modalService = inject(ModalService);
  private readonly toastService = inject(ToastService);

  readonly context = this.asideService.asideContext;
  readonly canManage = this.perms.canManageUsers;
  readonly canViewRoles = this.perms.canViewUserRoles;
  readonly currentUserId = computed(() => this.authService.user()?.uid);
  readonly roleLabels = ROLE_LABELS;

  readonly member = computed(() => {
    const ctx = this.context();
    if (ctx?.type !== 'view-team-member') return null;
    const memberId = (ctx.data as ViewTeamMemberAsideData).id;
    return this.userService.getUserById(memberId) || null;
  });

  getRoleBadgeVariant(role: UserRole): ColorVariant {
    switch (role) {
      case 'root':
      case 'admin':
        return 'primary';
      case 'dispatch_manager':
      case 'assets_manager':
      case 'dispatcher':
        return 'info';
      case 'accounting':
        return 'success';
      case 'safety':
        return 'warning';
      case 'maintenance':
        return 'secondary';
      case 'driver':
        return 'info';
      case 'owner_operator':
        return 'success';
      default:
        return 'default';
    }
  }

  /**
   * Normalize createdAt to a value the date pipe can handle.
   * The driver app writes serverTimestamp() → Firestore Timestamp {seconds, nanoseconds}.
   * Admin-created users write Date.now() → plain number (milliseconds).
   */
  getCreatedAt(user: any): number | null {
    const raw = user.createdAt;
    if (!raw) return null;
    // Firestore Timestamp object has a `seconds` field
    if (typeof raw === 'object' && raw.seconds) {
      return raw.seconds * 1000; // convert to ms
    }
    // Already a millisecond timestamp
    return typeof raw === 'number' ? raw : null;
  }

  editRoles(): void {
    const user = this.member();
    if (!user) return;
    this.modalService.open(EditRolesModalComponent, {
      title: 'Edit Roles',
      data: { userId: user.id },
    });
  }

  async removeUser(): Promise<void> {
    const user = this.member();
    if (!user || user.id === this.currentUserId()) return;

    const confirmed = await this.modalService.confirm({
      title: 'Remove Team Member',
      message: 'Are you sure you want to remove this member from your team?',
      confirmText: 'Remove Member',
      variant: 'destructive'
    });
    if (!confirmed) return;

    try {
      await this.userService.removeUserFromCompany(user.id);
      this.asideService.close();
      this.toastService.success('User removed from company.');
    } catch (err: any) {
      this.toastService.error(err.message || 'Failed to remove user.');
    }
  }
}
